import { useEffect, useState, useRef } from "react";
import { updateDoc, doc } from "firebase/firestore";
import { db, auth,storage } from "../firebase";
import { X, Trash, Check, Pencil } from "lucide-react";
import Swal from "sweetalert2";
import { containsBadWords } from "../utils/contentFilter";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const categories = [
  "Tecnología",
  "Hogar",
  "Libros",
  "Ropa",
  "Accesorios",
  "Materiales",
  "Instrumentos",
];

const conditions = ["Nuevo", "Usado", "Reacondicionado"];

const deliveryOptions = ["Recoger en el campus", "Envío", "Retiro personal"];

const paymentMethodsOptions = [
  "Efectivo",
  "Transferencia",
  "Tarjeta",
  "Paypal",
];

const warrantyOptions = ["No", "1 mes", "3 meses", "6 meses", "1 año"];

const statusOptions = [
  "Disponible",
  "Vendido",
  "Casi agotados",
  "Pendiente",
];

export default function AdminMarketModal({ onClose, allProducts, setProducts, interestedProposals = [] }) {
  const [userProducts, setUserProducts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const fileInputRef = useRef(null);

  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    price: 0,
    category: "",
    condition: "",
    quantity: 1,
    delivery: "",
    paymentMethods: [],
    warranty: "",
    status: "",
    media: [],
  });

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const ownProducts = allProducts.filter(
      (item) => item.userId === uid && item.status !== "eliminado"
    );
    setUserProducts(ownProducts);
  }, [allProducts]);

  const updateProductStatus = async (id, status) => {
    const ref = doc(db, "market", id);
    await updateDoc(ref, { status });
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status } : p))
    );
  };

  const startEditing = (product) => {
    setEditingId(product.id);
    setEditForm({
      title: product.title || "",
      description: product.description || "",
      price: product.price || 0,
      category: product.category || "",
      condition: product.condition || "",
      quantity: product.quantity || 1,
      delivery: product.delivery || "",
      paymentMethods: product.paymentMethods || [],
      warranty: product.warranty || "",
      status: product.status || "disponible",
      media: product.media || [],
    });
  };

  const handleAddImages = async (e) => {
  const files = Array.from(e.target.files);
  if (!files.length) return;

  const uploadedUrls = [];

  try {
    for (const file of files) {
      const filePath = `market/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      uploadedUrls.push(url);
    }

    setEditForm((f) => ({
      ...f,
      media: [...f.media, ...uploadedUrls],
    }));

    e.target.value = ""; // Reset input para poder volver a subir mismos archivos si se desea
  } catch (err) {
    console.error("Error subiendo imágenes:", err);
    Swal.fire("Error", "No se pudieron subir las imágenes.", "error");
  }
};

  const saveEdit = async (id) => {
  const trimmedTitle = editForm.title?.trim() || "";
  const trimmedDescription = editForm.description?.trim() || "";

  if (!trimmedTitle || !trimmedDescription) {
    return Swal.fire("Error", "El título y la descripción son obligatorios.", "warning");
  }

  if (containsBadWords(trimmedTitle)) {
    return Swal.fire("Error", "El título contiene palabras ofensivas o inapropiadas.", "error");
  }

  if (containsBadWords(trimmedDescription)) {
    return Swal.fire("Error", "La descripción contiene palabras ofensivas o inapropiadas.", "error");
  }

  try {
    const ref = doc(db, "market", id);
    await updateDoc(ref, { ...editForm, title: trimmedTitle, description: trimmedDescription });

    setProducts((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, ...editForm, title: trimmedTitle, description: trimmedDescription } : p
      )
    );

    setEditingId(null);
    Swal.fire("Éxito", "Producto actualizado correctamente.", "success");
  } catch (err) {
    console.error("Error al actualizar el producto:", err);
    Swal.fire("Error", "No se pudo actualizar el producto.", "error");
  }
};


  const updateImageAtIndex = (index, url) => {
    const newMedia = [...editForm.media];
    newMedia[index] = url;
    setEditForm((f) => ({ ...f, media: newMedia }));
  };

  const removeImageAtIndex = (index) => {
    const newMedia = [...editForm.media];
    newMedia.splice(index, 1);
    setEditForm((f) => ({ ...f, media: newMedia }));
  };

  const togglePaymentMethod = (method) => {
    if (editForm.paymentMethods.includes(method)) {
      setEditForm((f) => ({
        ...f,
        paymentMethods: f.paymentMethods.filter((m) => m !== method),
      }));
    } else {
      setEditForm((f) => ({
        ...f,
        paymentMethods: [...f.paymentMethods, method],
      }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-center pt-20">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold dark:text-white">Tus publicaciones</h2>
          <button onClick={onClose}>
            <X size={24} className="text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {userProducts.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No has publicado nada aún.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {userProducts.map((product) => (
              <div
                key={product.id}
                className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg shadow-sm"
              >
                {editingId === product.id ? (
                  <>
                    {/* Imágenes */}
                    <div className="mb-2">
                      <label className="block font-semibold mb-1 dark:text-white">Imágenes</label>
                      {editForm.media.map((url, idx) => (
                        <div key={idx} className="flex items-center mb-2 gap-2">
                          <img
                            src={url || "https://via.placeholder.com/100?text=Sin+Imagen"}
                            alt={`Imagen ${idx + 1}`}
                            className="w-24 h-24 object-cover rounded border"
                          />
                          <button
                            onClick={() => removeImageAtIndex(idx)}
                            className="text-red-600 hover:text-red-800"
                            title="Eliminar imagen"
                            type="button"
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => fileInputRef.current.click()}
                        className="text-blue-600 hover:underline text-sm"
                        type="button"
                      >
                        + Agregar imagen
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept="image/*"
                          multiple
                          onChange={handleAddImages}
                          className="hidden"
                        />
                      </button>
                    </div>

                    {/* Título */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Título</label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full p-1 rounded border dark:bg-gray-700"
                      />
                    </div>

                    {/* Descripción */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Descripción</label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full p-1 rounded border dark:bg-gray-700"
                      />
                    </div>

                    {/* Categoría */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Categoría</label>
                      <select
                        value={editForm.category}
                        onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                        className="w-full p-1 rounded border dark:bg-gray-700"
                      >
                        <option value="">-- Selecciona --</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Condición */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Condición</label>
                      <select
                        value={editForm.condition}
                        onChange={(e) => setEditForm({ ...editForm, condition: e.target.value })}
                        className="w-full p-1 rounded border dark:bg-gray-700"
                      >
                        <option value="">-- Selecciona --</option>
                        {conditions.map((cond) => (
                          <option key={cond} value={cond}>
                            {cond}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Cantidad */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Cantidad</label>
                      <input
                        type="number"
                        min={1}
                        value={editForm.quantity}
                        onChange={(e) =>
                          setEditForm({ ...editForm, quantity: Number(e.target.value) })
                        }
                        className="w-full p-1 rounded border dark:bg-gray-700"
                      />
                    </div>

                    {/* Precio */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Precio</label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) =>
                          setEditForm({ ...editForm, price: Number(e.target.value) })
                        }
                        className="w-full p-1 rounded border dark:bg-gray-700"
                      />
                    </div>

                    {/* Delivery */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Delivery</label>
                      <select
                        value={editForm.delivery}
                        onChange={(e) => setEditForm({ ...editForm, delivery: e.target.value })}
                        className="w-full p-1 rounded border dark:bg-gray-700"
                      >
                        <option value="">-- Selecciona --</option>
                        {deliveryOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Métodos de pago */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Métodos de pago</label>
                      <div className="flex flex-wrap gap-2">
                        {paymentMethodsOptions.map((method) => (
                          <label key={method} className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={editForm.paymentMethods.includes(method)}
                              onChange={() => togglePaymentMethod(method)}
                            />
                            <span className="dark:text-white">{method}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Garantía */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Garantía</label>
                      <select
                        value={editForm.warranty}
                        onChange={(e) => setEditForm({ ...editForm, warranty: e.target.value })}
                        className="w-full p-1 rounded border dark:bg-gray-700"
                      >
                        <option value="">-- Selecciona --</option>
                        {warrantyOptions.map((w) => (
                          <option key={w} value={w}>
                            {w}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Estado */}
                    <div className="mb-2">
                      <label className="block font-semibold dark:text-white">Estado</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="w-full p-1 rounded border dark:bg-gray-700"
                      >
                        <option value="">-- Selecciona --</option>
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => saveEdit(product.id)}
                        className="flex items-center gap-1 px-3 py-1 rounded bg-green-600 text-white"
                      >
                        <Check size={16} /> Guardar
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="flex items-center gap-1 px-3 py-1 rounded bg-gray-400 text-black"
                      >
                        <X size={16} /> Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-2">
                      {product.media && product.media.length > 0 ? (
                        <img
                          src={product.media[0]}
                          alt={product.title}
                          className="w-full h-40 object-cover rounded"
                        />
                      ) : (
                        <div className="w-full h-40 bg-gray-300 flex items-center justify-center rounded">
                          <span className="text-gray-500">Sin imagen</span>
                        </div>
                      )}
                    </div>
                    <h3 className="font-semibold dark:text-white">{product.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                      {product.description}
                    </p>
                    <p className="font-bold dark:text-white">RD$ {product.price.toFixed(2)}</p>
                    <p className="text-sm dark:text-gray-300">Estado: {product.status}</p>

                    <div className="mt-3 flex gap-2 flex-wrap">
                      <button
                        onClick={() => startEditing(product)}
                        className="bg-blue-600 text-white px-3 py-1 rounded flex items-center gap-1"
                      >
                        <Pencil size={16} /> Editar
                      </button>

                      {product.status !== "Vendido" && (
                        <button
                          onClick={() => updateProductStatus(product.id, "Vendido")}
                          className="bg-red-600 text-white px-3 py-1 rounded"
                        >
                          Marcar como vendido
                        </button>
                      )}

                      {product.status !== "eliminado" && (
                        <button
                          onClick={() => updateProductStatus(product.id, "eliminado")}
                          className="bg-gray-600 text-white px-3 py-1 rounded"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
